import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  output,
  viewChild,
  input
} from "@angular/core";
import {CommonModule} from "@angular/common";
import {HttpClient, HttpHeaders, HttpResponse} from "@angular/common/http";
import {hypecastHttpContext} from "@app/core/http/http-context";
import {Subject} from "rxjs";
import {finalize, map, takeUntil, tap} from "rxjs/operators";

import {HypecastAuthenticationService} from "@app/shared/authentication/services/authentication.service";
import {ApiUrlService} from "@app/shared/services/api-url.service";
import {__} from "@app/shared/functions/object.functions";
import {Response} from "@app/shared/services/local/Response";
import {HttpCacheService} from "@app/core/http/http-cache.service";
import dayjs from "dayjs/esm";
import {LoadingDirective} from "../loading/loading.directive";

@Component({
    selector: "hypecast-image",
    templateUrl: "./image.component.html",
    styleUrls: ["./image.component.scss"],
    imports: [CommonModule, LoadingDirective],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImageComponent implements OnInit, OnChanges, OnDestroy {
  readonly loadingContainerClass = input("");
  readonly customClass = input("image");
  readonly customContainerClass = input("");
  readonly loadingClass = input("");
  readonly height = input("auto");
  readonly width = input("auto");
  readonly minHeight = input("initial");
  readonly defaultImage = input("assets/150x150.png");
  readonly version = input<string | number | null | undefined>(null);
  readonly type = input("");
  readonly src = input("");

  readonly isLoaded = output<boolean>();

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.allowDynamicSetting) {
      return;
    }
    const srcChange = changes["src"];
    const versionChange = changes["version"];
    if (
      (srcChange && !srcChange.firstChange) ||
      (versionChange && !versionChange.firstChange && !__.IsNullOrUndefinedOrEmpty(this.src()))
    ) {
      this.loadImage();
    }
  }

  readonly imageRef = viewChild.required<ElementRef<HTMLImageElement>>("image");

  isLoading = true;
  allowDynamicSetting = false;

  private didUseDefaultOnce = false;
  private presignedGuid: string | null = null;
  private presignedCacheKey: string | null = null;
  private didRetryPresign = false;

  private _destroy$?: Subject<void>;
  private get destroy$(): Subject<void> {
    return this._destroy$ ??= new Subject<void>();
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private httpClient: HttpClient,
    private authenticationService: HypecastAuthenticationService,
    private apiUrlService: ApiUrlService,
    private httpCacheService: HttpCacheService
  ) {}

  ngOnInit(): void {
    this.allowDynamicSetting = true;
    this.loadImage();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadImage(): void {
    this.isLoading = true;
    this.didUseDefaultOnce = false;
    this.didRetryPresign = false;
    this.presignedGuid = null;
    this.presignedCacheKey = null;

    this.registerImageEvents();

    if (__.IsNullOrUndefinedOrEmpty(this.src())) {
      this.setElementSrc(this.defaultImage());
      return;
    }

    if (this.isGuidLike(this.src())) {
      this.fetchPresignedUrlAndSetImage(this.src());
      return;
    }

    this.setElementSrc(this.src());
  }

  private registerImageEvents(): void {
    const nativeImg = this.imageRef().nativeElement;

    nativeImg.onload = () => this.finishLoading(true);
    nativeImg.onerror = () => {
      const presignedGuid = this.presignedGuid;
      if (!this.didRetryPresign && presignedGuid) {
        this.didRetryPresign = true;
        const presignedCacheKey = this.presignedCacheKey;
        if (presignedCacheKey) {
          this.httpCacheService.clearCache(presignedCacheKey);
        }
        this.fetchPresignedUrlAndSetImage(presignedGuid, true);
        return;
      }

      if (!this.didUseDefaultOnce) {
        this.didUseDefaultOnce = true;
        this.setElementSrc(this.defaultImage());
      } else {
        this.setElementSrc("assets/150x150.png");
      }
    };
  }

  private fetchPresignedUrlAndSetImage(guid: string, forceRefresh: boolean = false): void {
    const requestUrl = `${this.apiUrlService.getCdnUrl()}api/v1/files/${guid}/urls`;
    const cacheKey = __.IsNullOrUndefinedOrEmpty(this.version() as any)
      ? requestUrl
      : `${requestUrl}?v=${this.version()}`;

    this.presignedGuid = guid;
    this.presignedCacheKey = cacheKey;

    if (!forceRefresh) {
      const cachedResponse = this.httpCacheService.getCacheData(cacheKey) as HttpResponse<Response<string>>;
      if (cachedResponse && cachedResponse.body && cachedResponse.body.data) {
        this.setElementSrc(cachedResponse.body.data);
        return;
      }
    }

    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.authenticationService.getAccessToken()}`,
      "Cache-Control": "private, max-age=10000000",
    });

    this.httpClient.get<Response<string>>(
      requestUrl,
      {
        headers,
        responseType: "json" as const,
        observe: "response" as const,
        context: hypecastHttpContext({disableApiPrefix: true, skipErrorHandler: true}),
      },
    ).pipe(
      tap((res: HttpResponse<Response<string>>): any => {
        this.httpCacheService.setCacheData(cacheKey, res, new Date(), dayjs().add(30, "minute").toDate());
      }),
      map((res: HttpResponse<Response<string>>) => res.body?.data ?? this.defaultImage()),
      finalize(() => {}),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (presignedUrl: string) => this.setElementSrc(presignedUrl),
      error: () => this.setElementSrc(this.defaultImage()),
    });
  }

  private setElementSrc(srcValue: string): void {
    this.imageRef().nativeElement.src = srcValue;
  }

  private finishLoading(success: boolean): void {
    this.isLoading = false;
    this.cdr.detectChanges();
    this.isLoaded.emit(success);
  }

  private isGuidLike(value: string): boolean {
    return !!value && value.split(".")[0].length === 36;
  }
}
